import { Helmet } from 'react-helmet-async';
import { SITE_SETTINGS } from '../data/cms/siteSettings.generated';
import { STRUCTURED_DATA } from '../data/cms/structuredData.generated';
import { useLanguage } from '../contexts/LanguageContext';
import { LOCALE_META, absoluteLocaleUrl, isLocalizedPath, localeAlternates, type SiteLocale } from '../i18n/siteLocales';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonicalUrl?: string;
  pageType?: 'website' | 'article' | 'product' | 'service';
  location?: string;
  /** Emit `noindex, nofollow` instead of the default index directive. */
  noindex?: boolean;
  /** Extra JSON-LD node(s) appended to the @graph (e.g. Article, FAQPage). */
  extraSchema?: Record<string, unknown> | Record<string, unknown>[];
  /** hreflang cluster for pages with country variants. Defaults to en + x-default -> this page. */
  alternates?: { hrefLang: string; href: string }[];
}

// ---------------------------------------------------------------------------
// CMS-sourced org / product / site identity. Values come from the Payload
// `siteSettings` + `structuredData` globals via the build-time content fetch
// (src/data/cms/*.generated.ts). Edit them in the CMS, not here.
// ---------------------------------------------------------------------------
type SiteSettings = {
  siteName?: string;
  orgDescription?: string;
  logoUrl?: string;
  ogImageDefault?: string;
  social?: { platform?: string; url?: string }[];
  contact?: {
    phone?: string;
    email?: string;
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
  foundingDate?: string;
};
type StructuredData = {
  softwareApplication?: {
    name?: string;
    softwareVersion?: string;
    datePublished?: string;
    applicationCategory?: string;
    operatingSystem?: string[];
    offers?: { name: string; price: string; priceCurrency?: string; description?: string }[];
    featureList?: string[];
  };
  website?: { searchUrlTemplate?: string };
};

const SITE = SITE_SETTINGS as SiteSettings;
const SD = STRUCTURED_DATA as StructuredData;
const ORIGIN = 'https://trainzilla.app';
const SAME_AS = (SITE.social ?? []).map((s) => s.url).filter((u): u is string => !!u);

const DEFAULT_KEYWORDS =
  'personal trainer software, gym management software, fitness coaching software, personal training app, ' +
  'online coaching platform, fitness management platform, trainer booking system, fitness business software, ' +
  'workout planning app for trainers, nutrition coaching software, personal trainer scheduling software, ' +
  'manage fitness clients online, track client progress, automate fitness business, ' +
  'online fitness coaching, virtual personal training, workout plan creator, AI fitness coaching software';

function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.siteName ?? 'TrainZilla',
    description: SITE.orgDescription,
    url: ORIGIN,
    logo: { '@type': 'ImageObject', url: SITE.logoUrl ?? `${ORIGIN}/og-image.png` },
    sameAs: SAME_AS,
    address: {
      '@type': 'PostalAddress',
      addressLocality: SITE.contact?.addressLocality,
      addressRegion: SITE.contact?.addressRegion,
      addressCountry: SITE.contact?.addressCountry,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: SITE.contact?.phone,
      email: SITE.contact?.email,
      contactType: 'Customer Service',
      availableLanguage: ['English'],
    },
    foundingDate: SITE.foundingDate ?? '2024',
  };
}

function softwareSchema(description: string, canonicalUrl: string, ogImage: string) {
  const s = SD.softwareApplication ?? {};
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: s.name ?? 'TrainZilla',
    description,
    url: canonicalUrl,
    applicationCategory: s.applicationCategory ?? 'BusinessApplication',
    operatingSystem: (s.operatingSystem ?? ['Web', 'iOS', 'Android']).join(', '),
    downloadUrl: `${ORIGIN}/client-app`,
    screenshot: ogImage,
    softwareVersion: s.softwareVersion ?? '2.0',
    datePublished: s.datePublished ?? '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    publisher: { '@type': 'Organization', name: 'TrainZilla', url: ORIGIN },
    offers: (s.offers ?? []).map((o) => ({
      '@type': 'Offer',
      name: o.name,
      price: o.price,
      priceCurrency: o.priceCurrency ?? 'INR',
      availability: 'https://schema.org/InStock',
      description: o.description,
    })),
    inLanguage: ['en'],
    audience: {
      '@type': 'Audience',
      audienceType: 'Personal Trainers, Gym Owners, Fitness Professionals',
    },
    featureList: s.featureList ?? [],
  };
}

// No SearchAction: the site has no /search page, so advertising one made Google
// crawl the literal template URL (/search?q={search_term_string}). Sitelinks
// search box markup is also deprecated by Google.
function websiteSchema(description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.siteName ?? 'TrainZilla',
    description,
    url: ORIGIN,
    inLanguage: 'en',
    publisher: { '@type': 'Organization', name: 'TrainZilla' },
  };
}

export default function SEOHead({
  // Default/fallback title — kept identical to index.html <title> and
  // SEO_PAGES.home.title so the homepage reports one consistent title.
  title = 'TrainZilla — AI-Adjusted Coaching Plans | Personal Trainer Software',
  description = 'TrainZilla drafts and weekly-adjusts each client\'s workout and diet from their check-ins — you approve every change. Manage clients, accept UPI payments, and brand your own client app. Trusted by 150+ coaches. Start free.',
  keywords,
  ogImage = 'https://trainzilla.app/og-image.png',
  canonicalUrl = 'https://trainzilla.app',
  pageType = 'website',
  noindex = false,
  extraSchema,
  alternates,
}: SEOHeadProps) {
  const finalKeywords = keywords || DEFAULT_KEYWORDS;

  // Translated pages (/hi/…, /ar/…, /es/…): canonical is the page's own language URL and the hreflang
  // cluster lists every language version. Extra alternates from the page (e.g. country pages on the
  // homepage) are kept alongside.
  const { language, basePath } = useLanguage();
  const locale = language as SiteLocale;
  if (isLocalizedPath(basePath)) {
    canonicalUrl = absoluteLocaleUrl(basePath, locale);
    const langAlts = localeAlternates(basePath);
    const extra = (alternates ?? []).filter((a) => a.hrefLang !== 'x-default' && !langAlts.some((l) => l.hrefLang === a.hrefLang));
    alternates = [...langAlts, ...extra];
  }

  const graph: Record<string, unknown>[] = [
    organizationSchema(),
    softwareSchema(description, canonicalUrl, ogImage),
    websiteSchema(description),
  ];
  if (Array.isArray(extraSchema)) graph.push(...extraSchema);
  else if (extraSchema) graph.push(extraSchema);

  const combinedSchema = { '@context': 'https://schema.org', '@graph': graph };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="TrainZilla" />
      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, nofollow'
            : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />
      {/* Each page is its own English / default alternate — never the homepage. */}
      {!noindex &&
        (alternates ?? [{ hrefLang: 'en', href: canonicalUrl }, { hrefLang: 'x-default', href: canonicalUrl }]).map((a) => (
          <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
        ))}

      {/* Open Graph */}
      <meta property="og:type" content={pageType === 'article' ? 'article' : 'website'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="TrainZilla — Fitness Coach Platform" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="TrainZilla" />
      <meta property="og:locale" content={LOCALE_META[locale]?.ogLocale ?? 'en_US'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="TrainZilla — Fitness Coach Platform" />
      <meta name="twitter:site" content="@trainzilla_in" />
      <meta name="twitter:creator" content="@trainzilla_in" />
      <meta name="twitter:domain" content="trainzilla.app" />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(combinedSchema)}</script>
    </Helmet>
  );
}
